import React from 'react';
import { Link } from 'react-router-dom';
import TravelTerms from '../components/TravelTerms';
import SeoHead from '../components/SeoHead';
import { CORE_PAGE_SEO } from '../data/corePageSeo';
import { CUSTOMER_SUPPORT } from '../data/customerPolicies';
import {
  DRIVER_BATA,
  MIN_ONEWAY_KM,
  VEHICLES,
  getVehiclePrice,
} from '../data/bookingConfig';
import '../styles/TariffPage.css';

const VEHICLE_IMAGES = {
  Sedan: '/images/vehicle/suzuki-dzire-1.webp',
  SUV: '/images/innova-car.webp',
  MUV: '/images/innova-crysta.webp',
};

const CARD_DETAILS = {
  Sedan: {
    title: 'Sedan',
    models: 'Dzire, Amaze, Aura, Etios',
    seats: '4 Passenger Seats + 1 Driver',
    badge: 'Most Booked',
    book: 'Book Sedan',
  },
  SUV: {
    title: 'SUV',
    models: 'Ertiga, Rumion, Carens, Innova',
    seats: '6/7 Passenger Seats + 1 Driver',
    badge: 'Family Choice',
    book: 'Book SUV',
  },
  MUV: {
    title: 'Innova Crysta',
    models: '',
    seats: '7 Passenger Seats + 1 Driver',
    badge: 'Premium Comfort',
    book: 'Book Crysta',
  },
};

const FEATURES = [
  {
    label: 'Climate-Controlled Comfort',
    icon: (
      <>
        <path d="m10 20-1.25-2.5L6 18" />
        <path d="M10 4 8.75 6.5 6 6" />
        <path d="m14 20 1.25-2.5L18 18" />
        <path d="m14 4 1.25 2.5L18 6" />
        <path d="m17 21-3-6h-4" />
        <path d="m17 3-3 6 1.5 3" />
        <path d="M2 12h6.5L10 9" />
        <path d="m20 10-1.5 2 1.5 2" />
        <path d="M22 12h-6.5L14 15" />
        <path d="m4 10 1.5 2L4 14" />
        <path d="m7 21 3-6-1.5-3" />
        <path d="m7 3 3 6h4" />
      </>
    ),
  },
  {
    label: 'Comfortable Seating',
    icon: (
      <>
        <path d="M19 9V6a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v3" />
        <path d="M3 16a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0v1.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V11a2 2 0 0 0-4 0z" />
        <path d="M5 18v2" />
        <path d="M19 18v2" />
      </>
    ),
  },
  {
    label: 'Ample Luggage Space',
    icon: (
      <>
        <path d="M6 20a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2" />
        <path d="M8 18V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v14" />
        <path d="M10 20h4" />
        <circle cx="16" cy="20" r="2" />
        <circle cx="8" cy="20" r="2" />
      </>
    ),
  },
];

const formatInr = (amount) => `₹${Number(amount).toLocaleString('en-IN')}`;

const Tariff = () => {
  const seo = CORE_PAGE_SEO.tariff;

  return (
    <main className="tariff-page">
      <SeoHead title={seo.title} description={seo.description} path={seo.path} />

      <section className="tariff-hero" aria-labelledby="tariff-heading">
        <div className="container">
          <p className="tariff-kicker"><span aria-hidden="true"></span>Tariff &amp; Terms</p>
          <h1 id="tariff-heading">Travel Better. Pay Fair.</h1>
          <p>Premium intercity travel with transparent pricing.</p>
        </div>
      </section>

      <section className="tariff-pricing" aria-labelledby="tariff-pricing-heading">
        <div className="container">
          <h2 id="tariff-pricing-heading">Vehicle pricing</h2>
          <div className="tariff-grid">
            {VEHICLES.map((vehicle) => {
              const details = CARD_DETAILS[vehicle.type];
              const minimumFare = getVehiclePrice(0, vehicle.rate, 'One Way');
              return (
                <article key={vehicle.type} className="tariff-card">
                  <p className="tariff-badge">{details.badge}</p>
                  <div className="tariff-card-media">
                    <img
                      src={VEHICLE_IMAGES[vehicle.type]}
                      alt={`${details.title} taxi`}
                      width="640"
                      height="360"
                      decoding="async"
                    />
                  </div>
                  <div className="tariff-card-body">
                    <div className="tariff-summary">
                      <h3>{details.title}</h3>
                      {details.models ? <p className="tariff-models">{details.models}</p> : null}
                      <p className="tariff-seats">{details.seats}</p>
                    </div>
                    <p className="tariff-price">
                      <span className="tariff-price-amount">₹{vehicle.rate}</span>
                      <span className="tariff-price-unit">/ km</span>
                    </p>
                    <p className="tariff-minimum">Estimated one-way fare {formatInr(minimumFare)}</p>
                    <ul className="tariff-features">
                      {FEATURES.map((feature) => (
                        <li key={feature.label}>
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            {feature.icon}
                          </svg>
                          {feature.label}
                        </li>
                      ))}
                    </ul>
                    <Link className="tariff-book" to="/#home-booking">{details.book}</Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="tariff-rules" aria-labelledby="tariff-rules-heading">
        <div className="container">
          <h2 id="tariff-rules-heading">Fare Rules &amp; Terms</h2>
          <p className="tariff-rules-lead">
            Estimated fare is billing distance × vehicle rate + driver allowance. Confirm the live total in the booking form for your pickup and drop.
          </p>
          <dl className="tariff-rule-list">
            <div>
              <dt>Minimum fare</dt>
              <dd>A one-way estimate starts at {MIN_ONEWAY_KM} km × the vehicle rate, plus {formatInr(DRIVER_BATA)} driver allowance. Each card shows that starting amount.</dd>
            </div>
            <div>
              <dt>Included kilometres</dt>
              <dd>One-way billing uses at least {MIN_ONEWAY_KM} km. A shorter mapped drive is still billed for {MIN_ONEWAY_KM} km.</dd>
            </div>
            <div>
              <dt>Extra kilometre charges</dt>
              <dd>Kilometres above that minimum use the same published per-kilometre rate. Round trips bill the one-way distance twice.</dd>
            </div>
            <div>
              <dt>Driver allowance</dt>
              <dd>{formatInr(DRIVER_BATA)} is added once to the estimate.</dd>
            </div>
            <div>
              <dt>Tolls and parking</dt>
              <dd>Toll, parking, permit, hill, and waiting charges may be extra when they apply.</dd>
            </div>
          </dl>
          <p className="tariff-rules-note">
            Questions about a rate? Call{' '}
            <a href={CUSTOMER_SUPPORT.phoneTel}>{CUSTOMER_SUPPORT.phoneDisplay}</a>.
          </p>
        </div>
      </section>

      <TravelTerms />
    </main>
  );
};

export default Tariff;
